import { configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';

import type { AccountSettingsGateway } from '@/features/account/account-settings/infrastructure';
import { httpAccountSettingsGateway } from '@/features/account/account-settings/infrastructure';
import type { EmailChangeGateway } from '@/features/account/email-change/infrastructure';
import { httpEmailChangeGateway } from '@/features/account/email-change/infrastructure';
import type { LinkedinCookiesGateway } from '@/features/account/linkedin-cookies/infrastructure';
import { httpLinkedinCookiesGateway } from '@/features/account/linkedin-cookies/infrastructure';
import type { NotificationPreferencesGateway } from '@/features/account/notification-preferences/infrastructure';
import { localNotificationPreferencesGateway } from '@/features/account/notification-preferences/infrastructure';
import type { PasswordChangeGateway } from '@/features/account/password-change/infrastructure';
import { httpPasswordChangeGateway } from '@/features/account/password-change/infrastructure';
import type { SecuritySessionsGateway } from '@/features/account/security-sessions/infrastructure';
import { httpSecuritySessionsGateway } from '@/features/account/security-sessions/infrastructure';
import type { AddAdministratorGateway } from '@/features/administrator/add-administrator/infrastructure';
import { httpAddAdministratorGateway } from '@/features/administrator/add-administrator/infrastructure';
import type { AdministratorsGateway } from '@/features/administrator/administrators/infrastructure';
import { httpAdministratorsGateway } from '@/features/administrator/administrators/infrastructure';
import type { EditAdministratorGateway } from '@/features/administrator/edit-administrator/infrastructure';
import { httpEditAdministratorGateway } from '@/features/administrator/edit-administrator/infrastructure';
import type { ApiKeysGateway } from '@/features/api-key/api-keys/infrastructure';
import { httpApiKeysGateway } from '@/features/api-key/api-keys/infrastructure';
import type { EditApiKeyGateway } from '@/features/api-key/edit-api-key/infrastructure';
import { httpEditApiKeyGateway } from '@/features/api-key/edit-api-key/infrastructure';
import type { ConfirmEmailChangeGateway } from '@/features/auth/confirm-email-change/infrastructure';
import { httpConfirmEmailChangeGateway } from '@/features/auth/confirm-email-change/infrastructure';
import type { ForgotPasswordGateway } from '@/features/auth/forgot-password/infrastructure';
import { httpForgotPasswordGateway } from '@/features/auth/forgot-password/infrastructure';
import type { LoginGateway } from '@/features/auth/login/infrastructure';
import { httpLoginGateway } from '@/features/auth/login/infrastructure';
import type { OnboardingGateway } from '@/features/auth/onboarding/infrastructure';
import { httpOnboardingGateway } from '@/features/auth/onboarding/infrastructure';
import type { ResetPasswordGateway } from '@/features/auth/reset-password/infrastructure';
import { httpResetPasswordGateway } from '@/features/auth/reset-password/infrastructure';
import type { ProfileTypesGateway } from '@/features/classification/profile-types/infrastructure';
import { httpProfileTypesGateway } from '@/features/classification/profile-types/infrastructure';
import type { CompaniesGateway } from '@/features/company/companies/infrastructure';
import { httpCompaniesGateway } from '@/features/company/companies/infrastructure';
import type { CompanyDetailGateway } from '@/features/company/company-detail/infrastructure';
import { httpCompanyDetailGateway } from '@/features/company/company-detail/infrastructure';
import type { InstitutionalDetailGateway } from '@/features/company/institutional-detail/infrastructure';
import { httpInstitutionalDetailGateway } from '@/features/company/institutional-detail/infrastructure';
import type { InstitutionalsGateway } from '@/features/company/institutionals/infrastructure';
import { httpInstitutionalsGateway } from '@/features/company/institutionals/infrastructure';
import type { DashboardGateway } from '@/features/dashboard/dashboard/infrastructure';
import { httpDashboardGateway } from '@/features/dashboard/dashboard/infrastructure';
import type { MetricsGateway } from '@/features/enrichment/metrics/infrastructure';
import { httpMetricsGateway } from '@/features/enrichment/metrics/infrastructure';
import type { SessionDetailGateway } from '@/features/enrichment/session-detail/infrastructure';
import { httpSessionDetailGateway } from '@/features/enrichment/session-detail/infrastructure';
import type { SessionsGateway } from '@/features/enrichment/sessions/infrastructure';
import { httpSessionsGateway } from '@/features/enrichment/sessions/infrastructure';
import { type FaqGateway, httpFaqGateway } from '@/features/faq/_shared/infrastructure';
import type { AddFundraisingGateway } from '@/features/fundraising/add-fundraising/infrastructure';
import { httpAddFundraisingGateway } from '@/features/fundraising/add-fundraising/infrastructure';
import type { EditFundraisingGateway } from '@/features/fundraising/edit-fundraising/infrastructure';
import { httpEditFundraisingGateway } from '@/features/fundraising/edit-fundraising/infrastructure';
import type { FundraisingsGateway } from '@/features/fundraising/fundraisings/infrastructure';
import { httpFundraisingsGateway } from '@/features/fundraising/fundraisings/infrastructure';
import type { AddLicenceGateway } from '@/features/licence/add-licence/infrastructure';
import { httpAddLicenceGateway } from '@/features/licence/add-licence/infrastructure';
import type { LicenceDetailGateway } from '@/features/licence/licence-detail/infrastructure';
import { httpLicenceDetailGateway } from '@/features/licence/licence-detail/infrastructure';
import type { LicencesGateway } from '@/features/licence/licences/infrastructure';
import { httpLicencesGateway } from '@/features/licence/licences/infrastructure';
import type { MyLicenceGateway } from '@/features/licence/my-licence/infrastructure';
import { httpMyLicenceGateway } from '@/features/licence/my-licence/infrastructure';
import type { AddListGateway } from '@/features/list/add-list/infrastructure';
import { httpAddListGateway } from '@/features/list/add-list/infrastructure';
import type { ClientImportAlertsGateway } from '@/features/list/client-import-alerts/infrastructure';
import { httpClientImportAlertsGateway } from '@/features/list/client-import-alerts/infrastructure';
import type { DiscoveredLpDetailGateway } from '@/features/list/discovered-lp-detail/infrastructure';
import { httpDiscoveredLpDetailGateway } from '@/features/list/discovered-lp-detail/infrastructure';
import type { DiscoveredLpsGateway } from '@/features/list/discovered-lps/infrastructure';
import { httpDiscoveredLpsGateway } from '@/features/list/discovered-lps/infrastructure';
import type { EditListGateway } from '@/features/list/edit-list/infrastructure';
import { httpEditListGateway } from '@/features/list/edit-list/infrastructure';
import type { FileImportGateway } from '@/features/list/file-import/infrastructure';
import { httpFileImportGateway } from '@/features/list/file-import/infrastructure';
import type { ImportsGateway } from '@/features/list/imports/infrastructure';
import { httpImportsGateway } from '@/features/list/imports/infrastructure';
import type { ListDetailGateway } from '@/features/list/list-detail/infrastructure';
import { httpListDetailGateway } from '@/features/list/list-detail/infrastructure';
import type { ListFeedbacksGateway } from '@/features/list/list-feedbacks/infrastructure';
import { httpListFeedbacksGateway } from '@/features/list/list-feedbacks/infrastructure';
import type { ListsGateway } from '@/features/list/lists/infrastructure';
import { httpListsGateway } from '@/features/list/lists/infrastructure';
import type { DeactivatedProfilesGateway } from '@/features/profile/deactivated-profiles/infrastructure';
import { httpDeactivatedProfilesGateway } from '@/features/profile/deactivated-profiles/infrastructure';
import type { EditProfileIdentityGateway } from '@/features/profile/edit-profile-identity/infrastructure';
import { httpEditProfileIdentityGateway } from '@/features/profile/edit-profile-identity/infrastructure';
import type { MyProfilesGateway } from '@/features/profile/my-profiles/infrastructure';
import { httpMyProfilesGateway } from '@/features/profile/my-profiles/infrastructure';
import { httpNameRulesGateway, type NameRulesGateway } from '@/features/profile/name-rules/infrastructure';
import type { ProfileCleaningGateway } from '@/features/profile/profile-cleaning/infrastructure';
import { httpProfileCleaningGateway } from '@/features/profile/profile-cleaning/infrastructure';
import type { ProfileDetailGateway } from '@/features/profile/profile-detail/infrastructure';
import { httpProfileDetailGateway } from '@/features/profile/profile-detail/infrastructure';
import type { ProfileExportGateway } from '@/features/profile/profile-export/infrastructure';
import { httpProfileExportGateway } from '@/features/profile/profile-export/infrastructure';
import type { ProfilesGateway } from '@/features/profile/profiles/infrastructure';
import { httpProfilesGateway } from '@/features/profile/profiles/infrastructure';
import type { TrackedLpsGateway } from '@/features/profile/tracked-lps/infrastructure';
import { httpTrackedLpsGateway } from '@/features/profile/tracked-lps/infrastructure';
import type { MySchoolsGateway } from '@/features/school/my-schools/infrastructure';
import { httpMySchoolsGateway } from '@/features/school/my-schools/infrastructure';
import type { SchoolsGateway } from '@/features/school/schools/infrastructure';
import { httpSchoolsGateway } from '@/features/school/schools/infrastructure';
import type { AddUserGateway } from '@/features/user/add-user/infrastructure';
import { httpAddUserGateway } from '@/features/user/add-user/infrastructure';
import type { EditUserGateway } from '@/features/user/edit-user/infrastructure';
import { httpEditUserGateway } from '@/features/user/edit-user/infrastructure';
import type { UsersGateway } from '@/features/user/users/infrastructure';
import { httpUsersGateway } from '@/features/user/users/infrastructure';

import { appNotifier, type Notifier } from './notifier';
import { rootReducer, type RootState } from './root-reducer';
import { rootSaga } from './root-saga';

/**
 * Dependencies injected into sagas via the middleware `context`. One gateway per
 * migrated feature, read by that feature's saga through `getContext(...)`.
 */
export interface SagaContext {
  accountSettingsGateway: AccountSettingsGateway;
  dashboardGateway: DashboardGateway;
  securitySessionsGateway: SecuritySessionsGateway;
  passwordChangeGateway: PasswordChangeGateway;
  emailChangeGateway: EmailChangeGateway;
  notificationPreferencesGateway: NotificationPreferencesGateway;
  linkedinCookiesGateway: LinkedinCookiesGateway;
  forgotPasswordGateway: ForgotPasswordGateway;
  resetPasswordGateway: ResetPasswordGateway;
  confirmEmailChangeGateway: ConfirmEmailChangeGateway;
  onboardingGateway: OnboardingGateway;
  addAdministratorGateway: AddAdministratorGateway;
  editAdministratorGateway: EditAdministratorGateway;
  administratorsGateway: AdministratorsGateway;
  apiKeysGateway: ApiKeysGateway;
  editApiKeyGateway: EditApiKeyGateway;
  loginGateway: LoginGateway;
  addListGateway: AddListGateway;
  discoveredLpsGateway: DiscoveredLpsGateway;
  discoveredLpDetailGateway: DiscoveredLpDetailGateway;
  editListGateway: EditListGateway;
  fileImportGateway: FileImportGateway;
  importsGateway: ImportsGateway;
  addLicenceGateway: AddLicenceGateway;
  licencesGateway: LicencesGateway;
  licenceDetailGateway: LicenceDetailGateway;
  myLicenceGateway: MyLicenceGateway;
  listsGateway: ListsGateway;
  listDetailGateway: ListDetailGateway;
  listFeedbacksGateway: ListFeedbacksGateway;
  clientImportAlertsGateway: ClientImportAlertsGateway;
  profilesGateway: ProfilesGateway;
  myProfilesGateway: MyProfilesGateway;
  trackedLpsGateway: TrackedLpsGateway;
  deactivatedProfilesGateway: DeactivatedProfilesGateway;
  profileDetailGateway: ProfileDetailGateway;
  editProfileIdentityGateway: EditProfileIdentityGateway;
  profileExportGateway: ProfileExportGateway;
  profileCleaningGateway: ProfileCleaningGateway;
  profileTypesGateway: ProfileTypesGateway;
  nameRulesGateway: NameRulesGateway;
  faqGateway: FaqGateway;
  usersGateway: UsersGateway;
  addUserGateway: AddUserGateway;
  editUserGateway: EditUserGateway;
  companiesGateway: CompaniesGateway;
  schoolsGateway: SchoolsGateway;
  mySchoolsGateway: MySchoolsGateway;
  companyDetailGateway: CompanyDetailGateway;
  institutionalsGateway: InstitutionalsGateway;
  institutionalDetailGateway: InstitutionalDetailGateway;
  sessionsGateway: SessionsGateway;
  sessionDetailGateway: SessionDetailGateway;
  metricsGateway: MetricsGateway;
  fundraisingsGateway: FundraisingsGateway;
  addFundraisingGateway: AddFundraisingGateway;
  editFundraisingGateway: EditFundraisingGateway;
  /** Error toasts raised by a saga, whatever page is on screen (#743). */
  notifier: Notifier;
}

/** Per-feature gateway overrides for tests (each defaults to its real HTTP impl). */
export interface MakeStoreGateways {
  accountSettingsGateway?: AccountSettingsGateway;
  dashboardGateway?: DashboardGateway;
  securitySessionsGateway?: SecuritySessionsGateway;
  passwordChangeGateway?: PasswordChangeGateway;
  emailChangeGateway?: EmailChangeGateway;
  notificationPreferencesGateway?: NotificationPreferencesGateway;
  linkedinCookiesGateway?: LinkedinCookiesGateway;
  forgotPasswordGateway?: ForgotPasswordGateway;
  resetPasswordGateway?: ResetPasswordGateway;
  confirmEmailChangeGateway?: ConfirmEmailChangeGateway;
  onboardingGateway?: OnboardingGateway;
  addAdministratorGateway?: AddAdministratorGateway;
  editAdministratorGateway?: EditAdministratorGateway;
  administratorsGateway?: AdministratorsGateway;
  apiKeysGateway?: ApiKeysGateway;
  editApiKeyGateway?: EditApiKeyGateway;
  loginGateway?: LoginGateway;
  addListGateway?: AddListGateway;
  discoveredLpsGateway?: DiscoveredLpsGateway;
  discoveredLpDetailGateway?: DiscoveredLpDetailGateway;
  editListGateway?: EditListGateway;
  fileImportGateway?: FileImportGateway;
  importsGateway?: ImportsGateway;
  addLicenceGateway?: AddLicenceGateway;
  licencesGateway?: LicencesGateway;
  licenceDetailGateway?: LicenceDetailGateway;
  myLicenceGateway?: MyLicenceGateway;
  listsGateway?: ListsGateway;
  listDetailGateway?: ListDetailGateway;
  listFeedbacksGateway?: ListFeedbacksGateway;
  clientImportAlertsGateway?: ClientImportAlertsGateway;
  profilesGateway?: ProfilesGateway;
  myProfilesGateway?: MyProfilesGateway;
  trackedLpsGateway?: TrackedLpsGateway;
  deactivatedProfilesGateway?: DeactivatedProfilesGateway;
  profileDetailGateway?: ProfileDetailGateway;
  editProfileIdentityGateway?: EditProfileIdentityGateway;
  profileExportGateway?: ProfileExportGateway;
  profileCleaningGateway?: ProfileCleaningGateway;
  profileTypesGateway?: ProfileTypesGateway;
  nameRulesGateway?: NameRulesGateway;
  faqGateway?: FaqGateway;
  usersGateway?: UsersGateway;
  addUserGateway?: AddUserGateway;
  editUserGateway?: EditUserGateway;
  companiesGateway?: CompaniesGateway;
  schoolsGateway?: SchoolsGateway;
  mySchoolsGateway?: MySchoolsGateway;
  companyDetailGateway?: CompanyDetailGateway;
  institutionalsGateway?: InstitutionalsGateway;
  institutionalDetailGateway?: InstitutionalDetailGateway;
  sessionsGateway?: SessionsGateway;
  sessionDetailGateway?: SessionDetailGateway;
  metricsGateway?: MetricsGateway;
  fundraisingsGateway?: FundraisingsGateway;
  addFundraisingGateway?: AddFundraisingGateway;
  editFundraisingGateway?: EditFundraisingGateway;
  /** Defaults to the app's notifier, plugged into the snackbar by `NotifierBridge`. */
  notifier?: Notifier;
}

/** Accept either a bare Add gateway (back-compat) or a per-feature gateways object. */
function normalizeGateways(arg: AddListGateway | MakeStoreGateways): MakeStoreGateways {
  return 'accountSettingsGateway' in arg ||
    'dashboardGateway' in arg ||
    'securitySessionsGateway' in arg ||
    'passwordChangeGateway' in arg ||
    'emailChangeGateway' in arg ||
    'notificationPreferencesGateway' in arg ||
    'linkedinCookiesGateway' in arg ||
    'forgotPasswordGateway' in arg ||
    'resetPasswordGateway' in arg ||
    'confirmEmailChangeGateway' in arg ||
    'onboardingGateway' in arg ||
    'addAdministratorGateway' in arg ||
    'editAdministratorGateway' in arg ||
    'administratorsGateway' in arg ||
    'apiKeysGateway' in arg ||
    'editApiKeyGateway' in arg ||
    'loginGateway' in arg ||
    'addListGateway' in arg ||
    'discoveredLpsGateway' in arg ||
    'discoveredLpDetailGateway' in arg ||
    'editListGateway' in arg ||
    'fileImportGateway' in arg ||
    'importsGateway' in arg ||
    'addLicenceGateway' in arg ||
    'licencesGateway' in arg ||
    'licenceDetailGateway' in arg ||
    'myLicenceGateway' in arg ||
    'listsGateway' in arg ||
    'listDetailGateway' in arg ||
    'listFeedbacksGateway' in arg ||
    'clientImportAlertsGateway' in arg ||
    'profilesGateway' in arg ||
    'myProfilesGateway' in arg ||
    'trackedLpsGateway' in arg ||
    'deactivatedProfilesGateway' in arg ||
    'profileDetailGateway' in arg ||
    'editProfileIdentityGateway' in arg ||
    'profileExportGateway' in arg ||
    'profileCleaningGateway' in arg ||
    'profileTypesGateway' in arg ||
    'nameRulesGateway' in arg ||
    'faqGateway' in arg ||
    'usersGateway' in arg ||
    'addUserGateway' in arg ||
    'editUserGateway' in arg ||
    'companiesGateway' in arg ||
    'schoolsGateway' in arg ||
    'mySchoolsGateway' in arg ||
    'companyDetailGateway' in arg ||
    'institutionalsGateway' in arg ||
    'institutionalDetailGateway' in arg ||
    'sessionsGateway' in arg ||
    'sessionDetailGateway' in arg ||
    'metricsGateway' in arg ||
    'fundraisingsGateway' in arg ||
    'addFundraisingGateway' in arg ||
    'editFundraisingGateway' in arg ||
    'notifier' in arg
    ? (arg as MakeStoreGateways)
    : { addListGateway: arg as AddListGateway };
}

/**
 * Build a store. Gateways are injected (default = real HTTP), so tests can pass
 * in-memory gateways and drive every scenario without a network. Pass a single
 * gateway for Add, or `{ addListGateway, editListGateway }` to override either.
 * `thunk: false` because side effects are handled by sagas, not thunks.
 */
export function makeStore(
  gateways: AddListGateway | MakeStoreGateways = httpAddListGateway,
  preloadedState?: Partial<RootState>,
) {
  const {
    accountSettingsGateway = httpAccountSettingsGateway,
    dashboardGateway = httpDashboardGateway,
    securitySessionsGateway = httpSecuritySessionsGateway,
    passwordChangeGateway = httpPasswordChangeGateway,
    emailChangeGateway = httpEmailChangeGateway,
    notificationPreferencesGateway = localNotificationPreferencesGateway,
    linkedinCookiesGateway = httpLinkedinCookiesGateway,
    forgotPasswordGateway = httpForgotPasswordGateway,
    resetPasswordGateway = httpResetPasswordGateway,
    confirmEmailChangeGateway = httpConfirmEmailChangeGateway,
    onboardingGateway = httpOnboardingGateway,
    addAdministratorGateway = httpAddAdministratorGateway,
    editAdministratorGateway = httpEditAdministratorGateway,
    administratorsGateway = httpAdministratorsGateway,
    apiKeysGateway = httpApiKeysGateway,
    editApiKeyGateway = httpEditApiKeyGateway,
    loginGateway = httpLoginGateway,
    addListGateway = httpAddListGateway,
    discoveredLpsGateway = httpDiscoveredLpsGateway,
    discoveredLpDetailGateway = httpDiscoveredLpDetailGateway,
    editListGateway = httpEditListGateway,
    fileImportGateway = httpFileImportGateway,
    importsGateway = httpImportsGateway,
    addLicenceGateway = httpAddLicenceGateway,
    licencesGateway = httpLicencesGateway,
    licenceDetailGateway = httpLicenceDetailGateway,
    myLicenceGateway = httpMyLicenceGateway,
    listsGateway = httpListsGateway,
    listDetailGateway = httpListDetailGateway,
    listFeedbacksGateway = httpListFeedbacksGateway,
    clientImportAlertsGateway = httpClientImportAlertsGateway,
    profilesGateway = httpProfilesGateway,
    myProfilesGateway = httpMyProfilesGateway,
    trackedLpsGateway = httpTrackedLpsGateway,
    deactivatedProfilesGateway = httpDeactivatedProfilesGateway,
    profileDetailGateway = httpProfileDetailGateway,
    editProfileIdentityGateway = httpEditProfileIdentityGateway,
    profileExportGateway = httpProfileExportGateway,
    profileCleaningGateway = httpProfileCleaningGateway,
    profileTypesGateway = httpProfileTypesGateway,
    nameRulesGateway = httpNameRulesGateway,
    faqGateway = httpFaqGateway,
    usersGateway = httpUsersGateway,
    addUserGateway = httpAddUserGateway,
    editUserGateway = httpEditUserGateway,
    companiesGateway = httpCompaniesGateway,
    schoolsGateway = httpSchoolsGateway,
    mySchoolsGateway = httpMySchoolsGateway,
    companyDetailGateway = httpCompanyDetailGateway,
    institutionalsGateway = httpInstitutionalsGateway,
    institutionalDetailGateway = httpInstitutionalDetailGateway,
    sessionsGateway = httpSessionsGateway,
    sessionDetailGateway = httpSessionDetailGateway,
    metricsGateway = httpMetricsGateway,
    fundraisingsGateway = httpFundraisingsGateway,
    addFundraisingGateway = httpAddFundraisingGateway,
    editFundraisingGateway = httpEditFundraisingGateway,
    notifier = appNotifier.notifier,
  } = normalizeGateways(gateways);
  const context: SagaContext = {
    accountSettingsGateway,
    dashboardGateway,
    securitySessionsGateway,
    passwordChangeGateway,
    emailChangeGateway,
    notificationPreferencesGateway,
    linkedinCookiesGateway,
    forgotPasswordGateway,
    resetPasswordGateway,
    confirmEmailChangeGateway,
    onboardingGateway,
    addAdministratorGateway,
    editAdministratorGateway,
    administratorsGateway,
    apiKeysGateway,
    editApiKeyGateway,
    loginGateway,
    addListGateway,
    discoveredLpsGateway,
    discoveredLpDetailGateway,
    editListGateway,
    fileImportGateway,
    importsGateway,
    addLicenceGateway,
    licencesGateway,
    licenceDetailGateway,
    myLicenceGateway,
    listsGateway,
    listDetailGateway,
    listFeedbacksGateway,
    clientImportAlertsGateway,
    profilesGateway,
    myProfilesGateway,
    trackedLpsGateway,
    deactivatedProfilesGateway,
    profileDetailGateway,
    editProfileIdentityGateway,
    profileExportGateway,
    profileCleaningGateway,
    profileTypesGateway,
    nameRulesGateway,
    faqGateway,
    usersGateway,
    addUserGateway,
    editUserGateway,
    companiesGateway,
    schoolsGateway,
    mySchoolsGateway,
    companyDetailGateway,
    institutionalsGateway,
    institutionalDetailGateway,
    sessionsGateway,
    sessionDetailGateway,
    metricsGateway,
    fundraisingsGateway,
    addFundraisingGateway,
    editFundraisingGateway,
    notifier,
  };
  const sagaMiddleware = createSagaMiddleware({ context });
  const store = configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: false,
        serializableCheck: {
          // The parse signals, and the profile photo upload (#579), deliberately
          // carry the uploaded `File` for the sagas; it never enters the state
          // (only the file name does, and nothing of the photo).
          ignoredActions: [
            'fileImport/parseRequested',
            'addList/fileParseRequested',
            'editList/fileParseRequested',
            'accountSettings/avatarUploadRequested',
            'listDetail/importEmailsRequested',
          ],
        },
      }).concat(sagaMiddleware),
  });
  sagaMiddleware.run(rootSaga);
  return store;
}

/** The singleton store used by the running app. */
export const store = makeStore();

export type AppStore = ReturnType<typeof makeStore>;
export type AppDispatch = AppStore['dispatch'];
export type { RootState };
